/* eslint-disable react/no-array-index-key */
import React, { memo, useCallback, useRef } from 'react';
import { HomefieldIconSearch } from '@dsg-tech/homefield-react-icons';
import { useAppDispatch, useAppSelector } from 'store/store';
import { setDisplayedSearchTerm } from 'store/slices/sayt';
import { breakpointSelector } from 'store/selectors';
import { SearchState } from '../../Search/types';

type KeywordSuggestionsProps = {
  suggestions: SearchState['suggestionResults'] | null;
  searchTerm: string;
  submitSearch: (term: string, type: string) => string;
  clickSuggestedTerm: (type: string) => void;
  setSearchTerm: (term: string) => void;
  searchFromSuggestion: (term: string, type: string) => void;
};

const HighlightText = ({ text, highlight }: { text: string; highlight: string }) => {
  if (!highlight) {
    return <span>{text}</span>;
  }
  // Split the search term into individual words
  const highlightWords = new Set(
    highlight
      .split(' ')
      .filter(Boolean)
      .map((word) => word.toLowerCase()),
  );
  const regex = new RegExp(`(${Array.from(highlightWords).join('|')})`, 'gi'); // Match any word in the search term

  const parts = text.split(regex);

  return (
    <span>
      {parts.map((part, i) =>
        highlightWords.has(part.toLowerCase()) ? (
          <span key={`highlight-words-${part}-${i}`} className="hmf-body-l matchingKeyword">
            {part}
          </span>
        ) : (
          <span key={`highlight-words-${part}-${i}`} className="hmf-body-bold-l nonMatchingKeyword">
            {part}
          </span> // Non-matching parts are returned with bold styling
        ),
      )}
    </span>
  );
};

const KeywordSuggestions = ({
  suggestions,
  searchTerm,
  submitSearch,
  clickSuggestedTerm,
  setSearchTerm,
  searchFromSuggestion,
}: KeywordSuggestionsProps) => {
  const viewPortBreakpoint = useAppSelector(breakpointSelector);
  const buttonRefArray = useRef<(HTMLButtonElement | null)[]>([]);

  const isSelectedAppliedRef = useRef(true);

  const dispatch = useAppDispatch();

  const handleSuggestionClick = useCallback(
    (
      e: React.MouseEvent<HTMLButtonElement, MouseEvent> | React.TouchEvent<HTMLButtonElement>,
      keyword: string,
    ) => {
      e.preventDefault();
      setSearchTerm(keyword);
      clickSuggestedTerm('keyword');
      window.location.href = submitSearch(keyword, 'keyword');
    },
    [setSearchTerm, clickSuggestedTerm, submitSearch],
  );

  const handleMouseEnter = useCallback(
    (keyword: string) => {
      if (searchTerm.length > 20) {
        searchFromSuggestion(keyword, 'keyword');
      }
    },
    [searchFromSuggestion],
  );

  // This function is to remove the selected class from the first button element when it has received focused
  const handleFocus = (index: number, term: string) => {
    if (index === 0 && isSelectedAppliedRef.current) {
      const button = buttonRefArray.current[index];
      if (button) {
        button.classList.remove('selected');
      }
      isSelectedAppliedRef.current = false;
    }
    dispatch(setDisplayedSearchTerm(term));
  };

  const isSelected = (index: number) => {
    const isDesktop = ['desktop', 'small-desktop'].includes(viewPortBreakpoint);
    return isSelectedAppliedRef.current && index === 0 && isDesktop;
  };

  return (
    <div className="sayt-keyword-suggestions-container" data-testid="sayt-keyword-suggestions">
      {suggestions?.keywords?.map((keyword: string, index: number) => (
        <div data-testid="sayt-suggested-keyword" key={`suggested-sayt-${keyword}-${index}`}>
          <button
            type="button"
            ref={(el) => {
              buttonRefArray.current[index] = el;
            }}
            className={`suggested-keyword hmf-display-flex hmf-align-items-center 
     ${isSelected(index) ? 'selected' : ''}
            `}
            onClick={(e) => handleSuggestionClick(e, keyword)}
            onMouseEnter={() => handleMouseEnter(keyword)}
            onFocus={() => {
              handleFocus(index, keyword);
              handleMouseEnter(keyword);
            }}
            aria-label={`search suggested keyword ${keyword}`}>
            <HomefieldIconSearch
              width={16}
              height={16}
              className="hmf-mr-xxs"
              aria-label="search suggested keyword"
            />
            <HighlightText text={keyword} highlight={searchTerm} />
          </button>
        </div>
      ))}
    </div>
  );
};

export default memo(KeywordSuggestions, (prev, next) => prev.suggestions === next.suggestions);
